/**
 * CounterOfferDialog - Modal form for submitting counter-offers on engaged offers.
 *
 * Pre-fills with the current offer terms. Available to both agents and investors.
 */

import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Dialog } from '@/common/components/dialog'
import { REDACT_ATTRS } from '@/common/analytics'
import { Button } from '@/common/components/button'
import { Input } from '@/common/components/input'
import { Select } from '@/common/components/select'
import { Textarea } from '@/common/components/textarea'
import { Text } from '@/common/components/text'
import { Stack } from '@/common/components/stack'
import { Form } from '@/common/components/form'
import type { ClosingTimeline, CounterOfferCreate } from '../../api/offers'

const CLOSING_TIMELINE_OPTIONS = [
  { value: '10_days', label: '10 days' },
  { value: '14_days', label: '14 days' },
  { value: '30_days', label: '30 days' },
  { value: 'flexible', label: 'Flexible' },
]

const counterOfferSchema = z.object({
  offer_price: z.coerce.number().positive('Offer price must be greater than 0'),
  earnest_money_deposit: z.coerce.number().min(0).max(100).optional(),
  closing_timeline: z.string().optional(),
  notes: z.string().max(2000).optional(),
})

type CounterOfferFormData = z.infer<typeof counterOfferSchema>

/**
 * The offer fields the dialog reads: the terms it prefills and the address it
 * names. Any offer shape carrying these fits, signed-in or partner link.
 */
export interface CounterOfferDialogOffer {
  id: string
  offer_price: number
  earnest_money_deposit?: number | null
  closing_timeline?: ClosingTimeline | null
  property?: {
    address?: {
      street_address?: string | null
      full_address?: string | null
    } | null
  } | null
}

export interface CounterOfferDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  offer: CounterOfferDialogOffer
  onSubmit: (offerId: string, data: CounterOfferCreate) => Promise<void>
}

export function CounterOfferDialog({
  open,
  onOpenChange,
  offer,
  onSubmit,
}: CounterOfferDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const form = useForm<CounterOfferFormData>({
    resolver: zodResolver(counterOfferSchema) as never,
    defaultValues: {
      offer_price: offer.offer_price,
      earnest_money_deposit: offer.earnest_money_deposit ?? undefined,
      closing_timeline: offer.closing_timeline ?? undefined,
      notes: '',
    },
  })

  const handleSubmit = async (data: CounterOfferFormData) => {
    setIsSubmitting(true)
    try {
      const counterOffer: CounterOfferCreate = {
        offer_price: data.offer_price,
      }
      if (data.earnest_money_deposit !== undefined) {
        counterOffer.earnest_money_deposit = data.earnest_money_deposit
      }
      if (data.closing_timeline) {
        counterOffer.closing_timeline = data.closing_timeline as ClosingTimeline
      }
      if (data.notes) {
        counterOffer.notes = data.notes
      }
      await onSubmit(offer.id, counterOffer)
      onOpenChange(false)
    } finally {
      setIsSubmitting(false)
    }
  }

  const propertyAddress = offer.property?.address?.street_address
    || offer.property?.address?.full_address
    || 'this property'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* This dialog is portal-rendered (outside the
          AuthenticatedLayout baseline), so it carries its own redaction at the
          content root. Both data-heap-redact-text and -redact-attributes
          cascade to descendants, so this single marking masks all text AND the
          listed attribute values across the whole dialog subtree — including
          the price/EMD <input value>s the baseline cannot reach. */}
      <Dialog.Content
        width={450}
        data-heap-redact-text="true"
        data-heap-redact-attributes={REDACT_ATTRS}
      >
        <Dialog.Header>
          <Dialog.Title>Counter Offer</Dialog.Title>
          <Dialog.Close />
        </Dialog.Header>

        <Text variant="bodySm" color="muted">
          Submit a counter-offer for {propertyAddress}.
        </Text>

        <Form form={form} onSubmit={handleSubmit}>
          <Stack gap="16">
            <Input
              name="offer_price"
              control={form.control}
              label="Offer Price"
              type="number"
              prefix="$"
              mask="currency"
            />
            <Input
              name="earnest_money_deposit"
              control={form.control}
              label="Earnest Money Deposit (%)"
              type="number"
              placeholder="0-100"
              optional
            />
            <Select
              name="closing_timeline"
              control={form.control}
              label="Closing Timeline"
              options={CLOSING_TIMELINE_OPTIONS}
              placeholder="Select timeline"
              optional
            />
            <Textarea
              name="notes"
              control={form.control}
              label="Notes"
              placeholder="Add notes about this counter-offer..."
              rows={3}
              optional
            />
          </Stack>

          <Dialog.Footer>
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Counter Offer'}
            </Button>
          </Dialog.Footer>
        </Form>
      </Dialog.Content>
    </Dialog>
  )
}
